import { httpsCallable } from 'firebase/functions';
import { signOut } from 'firebase/auth';
import { errorHandler } from '../utils/errorHandler';
import { auth, functions } from './firebase';
import { ROLES } from '../config/constants';

const getLegacyCallableName = (functionName) => `${functionName}-${functionName}`;

const isFunctionNotFoundError = (error) => {
    const message = String(error?.message || '').toLowerCase();
    return (
        error?.code === 'functions/not-found' ||
        message.includes('not found') ||
        message.includes('status code: 404') ||
        message.includes('404') ||
        message.includes('cors') ||
        message.includes('failed to fetch') ||
        message.includes('networkerror')
    );
};

const invokeCallable = async (name, data) => {
    const func = httpsCallable(functions, name);
    const response = await func(data);
    return response.data;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryableAppCheckError = (error) => {
    const message = String(error?.message || '').toLowerCase();
    const code = String(error?.code || '').toLowerCase();
    return (
        code.includes('unauthenticated') ||
        code.includes('permission-denied') ||
        message.includes('app check') ||
        message.includes('unauthenticated') ||
        message.includes('permission-denied') ||
        message.includes('401')
    );
};

const invokeCallableWithRetry = async (name, data) => {
    try {
        return await invokeCallable(name, data);
    } catch (error) {
        // Safari can hit transient first-call failures before App Check token is ready.
        if (!isRetryableAppCheckError(error)) {
            throw error;
        }
        await sleep(500);
        return await invokeCallable(name, data);
    }
};

// Helper function for calling Firebase Functions
const callFunction = async (functionName, data = {}) => {
    try {
        // If data is FormData, extract and convert for Firebase Functions
        let processedData = data;
        if (data instanceof FormData) {
            
            // Get the data field which contains our JSON string
            const jsonData = data.get('data');
            if (jsonData) {
                try {
                    processedData = JSON.parse(jsonData);
                    
                    // If there's a picture file, we need special handling
                    if (data.get('picture')) {
                        // For now, we're just preserving the fact there's a picture
                        // In a real app, you would upload this to storage first
                        processedData.hasPicture = true;
                    }
                } catch (e) {
                }
            }
        }

        const legacyName = getLegacyCallableName(functionName);
        const shouldSkipLegacy = functionName.includes('-');

        try {
            // Prefer legacy callable names first because current production
            // deployment appears to use the duplicated export format.
            if (!shouldSkipLegacy) {
                return await invokeCallableWithRetry(legacyName, processedData);
            }
            return await invokeCallableWithRetry(functionName, processedData);
        } catch (error) {
            if (!isFunctionNotFoundError(error)) {
                throw error;
            }
            // Fall back to modern callable name when legacy is unavailable.
            return await invokeCallableWithRetry(functionName, processedData);
        }
    } catch (error) {
        // Handle authentication errors
        if (error.code === 'auth/id-token-expired' || 
            error.code === 'auth/id-token-revoked') {
            await signOut(auth);
            throw error;
        }
        
        // Process the error through errorHandler but preserve original details
        const processedError = errorHandler.processError(error);
        
        // If the error is from Firebase Functions V2, ensure the code is preserved
        if (error.details && error.details.code) {
            processedError.code = error.details.code;
        }
        
        throw processedError;
    }
};

// Auth API
export const authAPI = {
    login: (email, password) => callFunction('login', { email, password }),
    register: (userData) => callFunction('register', userData),
    getCurrentUser: async () => {
        const user = auth.currentUser;
        if (!user) return null;
        
        // Force token refresh to get latest custom claims
        await user.getIdToken(true);
        const idTokenResult = await user.getIdTokenResult();

        return {
            uid: user.uid,
            email: user.email,
            emailVerified: user.emailVerified,
            role: idTokenResult.claims.role || ROLES.USER,
            isAdmin: idTokenResult.claims.role === ROLES.ADMIN || idTokenResult.claims.role === ROLES.SUPER_ADMIN,
            isSuperAdmin: idTokenResult.claims.role === ROLES.SUPER_ADMIN,
            hasAppliedToAcademy: idTokenResult.claims.hasAppliedToAcademy || false
        };
    },
    verifyUser: (idToken) => callFunction('verifyUser', { idToken }),
    updateProfile: (data) => callFunction('updateProfile', data),
    resetPassword: (email) => callFunction('resetPassword', { email }),
    getAllUsers: () => callFunction('getAllUsers', {}),
    clearUserCache: () => callFunction('clearUserCache', {}),
    sendVerificationEmail: (uid) => callFunction('sendVerificationEmail', { uid })
};

// Publications API
export const publicationsAPI = {
    getAllPublications: () => callFunction('getAllPublications', {}),
    getPublication: (id) => callFunction('getPublication', { id }),
    createPublication: (data) => callFunction('createPublication', data),
    updatePublication: (id, data) => callFunction('updatePublication', { id, ...data }),
    deletePublication: (id) => callFunction('deletePublication', { id })
};

// Activities API
export const activitiesAPI = {
    getAllActivities: () => callFunction('getAllActivities', {}),
    getActivity: (id) => callFunction('getActivity', { id }),
    createActivity: (data) => callFunction('createActivity', data),
    updateActivity: (id, activityData) => callFunction('updateActivity', { activityId: id, data: activityData }),
    deleteActivity: (id) => callFunction('deleteActivity', { id })
};

// Groups API
export const groupsAPI = {
    getAllGroups: () => callFunction('getAllGroups', {}),
    getGroup: (id) => callFunction('getGroup', { id }),
    createGroup: (data) => callFunction('createGroup', data),
    updateGroup: (id, data) => callFunction('updateGroup', { id, updates: data }),
    deleteGroup: (id) => callFunction('deleteGroup', { groupId: id })
};

// Clubs API
export const clubsAPI = {
    getAllClubs: () => callFunction('clubsGetAll', {}),
    getClub: (id) => callFunction('clubsGet', { id }),
    createClub: (data) => callFunction('clubsCreate', data),
    updateClub: (id, data) => callFunction('clubsUpdate', { id, ...data }),
    deleteClub: (id) => callFunction('clubsDelete', { id }),
    getAllGroups: () => callFunction('getAllGroups', {})
};
// Think Tank API
export const thinkTankAPI = {
    getThinkTankLead: () => callFunction('thinkTankGetLead', {}),
    setThinkTankLead: (userId) => callFunction('thinkTankSetLead', { userId }),
    join: (data) => callFunction('thinkTankJoin', data),
    joinThinkTank: (data) => callFunction('thinkTankJoin', data)
};

// Projects API
export const projectsAPI = {
    getAllProjects: () => callFunction('getAllProjects', {}),
    getProject: (id) => callFunction('getProject', { id }),
    createProject: (data) => callFunction('createProject', data),
    updateProject: (id, projectData) => callFunction('updateProject', { id, updates: projectData }),
    deleteProject: (id) => callFunction('deleteProject', { id })
};

// Counter API
export const counterAPI = {
    getCounter: () => callFunction('getCounter', {}),
    incrementCounter: () => callFunction('incrementCounter', {}),
    decrementCounter: () => callFunction('decrementCounter', {}),
    resetCounter: () => callFunction('resetCounter', {})
};

// Activity Log API
export const activityLogAPI = {
    getRecentActivity: () => callFunction('getRecentActivity', {})
};

// Academy API
export const academyAPI = {
    getApplication: async () => {
        const user = auth.currentUser;
        if (!user) throw new Error('User must be authenticated');
        return callFunction('getAcademyApplication', { userId: user.uid });
    },
    checkApplicationStatus: async () => {
        const user = auth.currentUser;
        if (!user) return { hasApplied: false };
        
        // Force token refresh to get latest custom claims
        await user.getIdToken(true);
        const idTokenResult = await user.getIdTokenResult(true);
        return { hasApplied: idTokenResult.claims.hasAppliedToAcademy || false };
    },
    submitApplication: async (data) => {
        const user = auth.currentUser;
        if (!user) throw new Error('User must be authenticated');
        return callFunction('submitAcademyApplication', data);
    },
    updateApplication: (data) => callFunction('updateAcademyApplication', data),
    getAllApplications: async () => {
        const response = await callFunction('getAllAcademyApplications');
        return response;
    },
    updateApplicationStatus: (applicationId, status) => callFunction('updateAcademyApplicationStatus', { applicationId, status }),
    deleteApplication: async (applicationId) => {
        if (!applicationId) throw new Error('Application ID is required');
        return callFunction('deleteAcademyApplication', { applicationId: applicationId });
    }
};

// Members API
export const membersAPI = {
    getAllMembers: (page = 1, limit = 12) => callFunction('getAllMembers', { page, limit }),
    createMember: (data) => callFunction('createMember', data),
    updateMember: (id, data) => callFunction('updateMember', { id, ...data }),
    deleteMember: (id) => callFunction('deleteMember', { id }),
    getMemberGroups: (memberId) => callFunction('getMemberGroups', { memberId }),
    getAllMemberGroups: () => callFunction('getAllMemberGroups'),
    updateMemberGroups: (memberId, groups) => callFunction('updateMemberGroups', { memberId, groups }),
    getMembersByIds: (memberIds) => callFunction('getMembersByIds', { memberIds })
};

// Events API
export const eventsAPI = {
    getEvent: (id) => callFunction('getEvent', { eventId: id }),
    createEvent: (data) => callFunction('createEvent', data),
    updateEvent: (id, data) => callFunction('updateEvent', { eventId: id, data }),
    deleteEvent: (id) => callFunction('deleteEvent', { eventId: id }),
    registerForEvent: (eventId) => callFunction('registerForEvent', { eventId }),
    getUpcomingEvents: () => callFunction('getUpcomingEvents', {})
};

// Home API
export const homeAPI = {
    subscribe: (email) => callFunction('subscribeToNewsletter', { email }),
    // Temporary compatibility with currently deployed function name.
    // Remove "-getHomePageData" once backend export/deploy is normalized.
    getHomePageData: () => callFunction('getHomePageData-getHomePageData')
};

// Partnership API
export const partnershipAPI = {
    submit: (data) => callFunction('submitPartnershipApplication', data),
    getAllApplications: () => callFunction('getAllPartnershipApplications'),
    updateStatus: (applicationId, status, feedback) => callFunction('updatePartnershipStatus', { applicationId, status, feedback }),
    get: (id) => callFunction('getPartnership', { id }),
    update: (id, data) => callFunction('updatePartnership', { id, ...data }),
    deletePartnershipApplication: (applicationId) => callFunction('deletePartnershipApplication', { applicationId }),
    updateNotifications: (data) => callFunction('updateNotifications', data)
};

// Admin API
export const adminAPI = {
    deleteUser: (uid) => callFunction('deleteUser', { userId: uid }),
    updateUserRole: (uid, role) => callFunction('updateUserRole', { uid, role }),
    getAllUsers: () => callFunction('getAllUsers', {}),
    getStats: () => callFunction('getAdminStats', {})
};

// Profile API
export const profileAPI = {
    getProfile: (data) => callFunction('getProfile', data),
    updateProfile: (data) => callFunction('updateProfile', data),
    uploadProfileImage: (data) => callFunction('uploadProfileImage', data),
    deleteProfileImage: (data) => callFunction('deleteProfileImage', data),
    getAcademyApplication: () => callFunction('getAcademyApplication')
}; 